/**
 * Centralizes publisher-ledger status semantics so every live-state view presents
 * sync and inventory state consistently without duplicating backend-aware logic.
 */
import type {
  EntityStatus,
  PublisherEntityDto,
  PublisherEntityType,
  SyncStatus,
} from 'src/store/generated/leela-api-generated';

export enum PublisherEntityBadge {
  LIVE = 'LIVE',
  PENDING = 'PENDING',
  FAILED = 'FAILED',
  SKIPPED = 'SKIPPED',
  REMOVED = 'REMOVED',
}

export const BADGE_LABELS: Record<PublisherEntityBadge, string> = {
  [PublisherEntityBadge.LIVE]: 'publisher_entity_status_live',
  [PublisherEntityBadge.PENDING]: 'publisher_entity_status_pending',
  [PublisherEntityBadge.FAILED]: 'publisher_entity_status_failed',
  [PublisherEntityBadge.SKIPPED]: 'publisher_entity_status_skipped',
  [PublisherEntityBadge.REMOVED]: 'publisher_entity_status_removed',
};

type PublisherEntityStatusFields = Pick<
PublisherEntityDto,
'publisherEntityType' | 'syncStatus' | 'entityStatus'
>;

function deriveSyncedStatus (
  publisherEntityType: PublisherEntityType,
  entityStatus: EntityStatus | undefined,
): PublisherEntityBadge {
  switch (publisherEntityType) {
    case 'AD':
    case 'ASSET':
      if (entityStatus === 'IN_STOCK') return PublisherEntityBadge.LIVE;
      if (entityStatus === 'OUT_OF_STOCK') return PublisherEntityBadge.REMOVED;
      return PublisherEntityBadge.PENDING;
    case 'CAMPAIGN':
    case 'AD_GROUP':
      return PublisherEntityBadge.LIVE;
    default:
      return PublisherEntityBadge.PENDING;
  }
}

export function derivePublisherEntityStatus (
  entity: PublisherEntityStatusFields,
): PublisherEntityBadge {
  const syncStatus: SyncStatus | undefined = entity.syncStatus;

  switch (syncStatus) {
    case 'SYNCED':
      return deriveSyncedStatus(entity.publisherEntityType, entity.entityStatus);
    case 'FAILED':
      return PublisherEntityBadge.FAILED;
    case 'SKIPPED':
      return PublisherEntityBadge.SKIPPED;
    case 'PENDING':
    case 'PROCESSING':
    default:
      return PublisherEntityBadge.PENDING;
  }
}

/** Sort order for entity status badges — LIVE first, then PENDING, FAILED, SKIPPED, REMOVED last. */
export const STATUS_SORT_ORDER: Record<PublisherEntityBadge, number> = {
  [PublisherEntityBadge.LIVE]: 0,
  [PublisherEntityBadge.PENDING]: 1,
  [PublisherEntityBadge.FAILED]: 2,
  [PublisherEntityBadge.SKIPPED]: 3,
  [PublisherEntityBadge.REMOVED]: 4,
};

/** Comparator: sorts entities by derived status (LIVE first). */
export function compareByStatus (a: PublisherEntityStatusFields, b: PublisherEntityStatusFields): number {
  return (STATUS_SORT_ORDER[derivePublisherEntityStatus(a)] ?? 4) - (STATUS_SORT_ORDER[derivePublisherEntityStatus(b)] ?? 4);
}
